import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { Header } from "@/components/Header";
import { apiFetch } from "@/lib/api";

const OPEN_TO = ["All", "Hiring", "Looking for work", "Mentoring", "Cofounder", "Speaking", "Advising"];

const Avatar = ({ person }) =>
  person.picture ? (
    <img src={person.picture} alt={person.name} className="h-12 w-12 shrink-0 rounded-full object-cover" />
  ) : (
    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-neutral-900 text-sm font-bold text-white">
      {person.name[0].toUpperCase()}
    </span>
  );

export default function Directory() {
  const [members, setMembers] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [openTo, setOpenTo] = useState("All");
  const [hood, setHood] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let stale = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (debouncedQ) params.set("q", debouncedQ);
    if (openTo !== "All") params.set("open_to", openTo);
    if (hood !== "All") params.set("neighborhood", hood);
    apiFetch(`/members?${params.toString()}`)
      .then((r) => {
        if (stale) return;
        setMembers(r.members);
        setNeighborhoods(r.neighborhoods);
        setLoading(false);
      })
      .catch(() => !stale && setLoading(false));
    return () => {
      stale = true;
    };
  }, [debouncedQ, openTo, hood]);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-4xl">Member directory</h1>
        <p className="mt-2 text-sm text-neutral-500 md:text-lg">
          Find people by skill, company or what they're open to.
        </p>

        <div className="mt-7 flex flex-wrap items-center gap-3">
          <div className="flex min-w-[260px] flex-1 items-center gap-2 rounded-full border border-neutral-300 px-4 py-2.5">
            <Search className="h-4 w-4 text-neutral-400" />
            <input
              data-testid="member-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, skill, company…"
              className="w-full bg-transparent text-sm outline-none"
            />
          </div>
          <select
            data-testid="neighborhood-filter"
            value={hood}
            onChange={(e) => setHood(e.target.value)}
            className="rounded-full border border-neutral-300 px-4 py-2.5 text-sm font-semibold"
          >
            <option value="All">Any neighborhood</option>
            {neighborhoods.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-4 flex flex-wrap gap-2" data-testid="open-to-filters">
          {OPEN_TO.map((o) => (
            <button
              key={o}
              data-testid={`filter-open-${o}`}
              onClick={() => setOpenTo(o)}
              className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                openTo === o
                  ? "border-neutral-900 bg-neutral-900 text-white"
                  : "border-neutral-200 text-neutral-700 hover:border-neutral-400"
              }`}
            >
              {o === "All" ? "Everyone" : o}
            </button>
          ))}
        </div>

        <p className="mt-6 text-sm font-semibold text-neutral-500" data-testid="member-count">
          {loading ? "Searching…" : `${members.length} member${members.length === 1 ? "" : "s"}`}
        </p>

        {loading ? (
          <ul className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <li key={i} className="h-28 animate-pulse rounded-2xl bg-neutral-100" />
            ))}
          </ul>
        ) : members.length === 0 ? (
          <p className="mt-4 rounded-2xl border border-dashed border-neutral-300 p-12 text-center text-sm text-neutral-500">
            Nobody matches those filters yet.
          </p>
        ) : (
          <ul className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="directory-grid">
            {members.map((m) => (
              <li key={m.user_id}>
                <Link
                  to={`/members/${m.user_id}`}
                  data-testid={`directory-${m.user_id}`}
                  className="flex h-full gap-3 rounded-2xl border border-neutral-200 p-5 transition-shadow duration-200 hover:shadow-[0_4px_16px_rgba(0,0,0,0.08)]"
                >
                  <Avatar person={m} />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-neutral-900">{m.name}</p>
                    <p className="mt-0.5 line-clamp-2 text-xs text-neutral-600">
                      {m.headline || (m.company ? m.company : "Member")}
                    </p>
                    {m.skills?.length > 0 && (
                      <p className="mt-2 flex flex-wrap gap-1">
                        {m.skills.slice(0, 3).map((s) => (
                          <span key={s} className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-semibold text-neutral-700">
                            {s}
                          </span>
                        ))}
                      </p>
                    )}
                    {m.open_to?.length > 0 && (
                      <p className="mt-2 text-[11px] font-bold text-[#4D7C0F]">
                        Open to {m.open_to.slice(0, 2).join(", ").toLowerCase()}
                      </p>
                    )}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
